/**
 * 同期マネージャー
 * オフライン時の保留中の変更をオンライン復帰時に同期
 */

import { supabase } from './supabase'
import { getPendingChanges, clearPendingChanges, setLastSyncTime, type PendingChange } from './offlineStorage'

/**
 * 保留中の変更を同期
 */
export const syncPendingChanges = async (): Promise<{ success: boolean; syncedCount: number; errors: string[] }> => {
  const pendingChanges = await getPendingChanges()

  if (pendingChanges.length === 0) {
    console.log('No pending changes to sync')
    return { success: true, syncedCount: 0, errors: [] }
  }

  console.log(`Syncing ${pendingChanges.length} pending changes...`)

  const errors: string[] = []
  let syncedCount = 0

  // タイムスタンプでソート（古い変更から順に適用）
  const sortedChanges = pendingChanges.sort((a, b) => a.timestamp - b.timestamp)

  for (const change of sortedChanges) {
    try {
      await syncSingleChange(change)
      syncedCount++
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      console.error(`Failed to sync change ${change.id}:`, errorMessage)
      errors.push(`${change.type} ${change.entity}: ${errorMessage}`)
    }
  }

  // すべて成功した場合のみクリア
  if (errors.length === 0) {
    await clearPendingChanges()
    await setLastSyncTime(Date.now())
    console.log(`Successfully synced ${syncedCount} changes`)
  } else {
    console.warn(`Synced ${syncedCount}/${sortedChanges.length} changes with ${errors.length} errors`)
  }

  return {
    success: errors.length === 0,
    syncedCount,
    errors
  }
}

/**
 * 単一の変更を同期
 */
const syncSingleChange = async (change: PendingChange): Promise<void> => {
  const { type, entity, data } = change

  if (entity === 'project') {
    await syncProjectChange(type, data)
  } else if (entity === 'todo') {
    await syncTodoChange(type, data)
  } else {
    throw new Error(`Unknown entity type: ${entity}`)
  }
}

/**
 * プロジェクトの変更を同期
 */
const syncProjectChange = async (type: PendingChange['type'], data: any): Promise<void> => {
  switch (type) {
    case 'create':
      // オフライン時に作成されたプロジェクトを挿入
      const { error: createError } = await supabase
        .from('projects')
        .insert([data])

      if (createError) throw createError
      break

    case 'update':
      // プロジェクトを更新
      const { id, ...updates } = data
      const { error: updateError } = await supabase
        .from('projects')
        .update(updates)
        .eq('id', id)

      if (updateError) throw updateError
      break

    case 'delete':
      // プロジェクトを削除
      const { error: deleteError } = await supabase
        .from('projects')
        .delete()
        .eq('id', data.id)

      if (deleteError) throw deleteError
      break

    default:
      throw new Error(`Unknown change type: ${type}`)
  }
}

/**
 * タスクの変更を同期
 */
const syncTodoChange = async (type: PendingChange['type'], data: any): Promise<void> => {
  switch (type) {
    case 'create':
      // オフライン時に作成されたタスクを挿入
      const { error: createError } = await supabase
        .from('todos')
        .insert([data])

      if (createError) throw createError
      break

    case 'update':
      // タスクを更新
      const { id, ...updates } = data
      const { error: updateError } = await supabase
        .from('todos')
        .update(updates)
        .eq('id', id)

      if (updateError) throw updateError
      break

    case 'delete':
      // タスクを削除
      const { error: deleteError } = await supabase
        .from('todos')
        .delete()
        .eq('id', data.id)

      if (deleteError) throw deleteError
      break

    default:
      throw new Error(`Unknown change type: ${type}`)
  }
}

/**
 * 競合を解決（last-write-wins戦略）
 *
 * 注: 現在の実装では、サーバー側のデータを上書きします。
 * より高度な競合解決が必要な場合は、タイムスタンプを比較して
 * 新しい方を採用するロジックを追加してください。
 */
export const resolveConflict = async (
  localData: any,
  serverData: any
): Promise<any> => {
  // シンプルな戦略: ローカルの変更を優先（last-write-wins）
  // localDataのタイムスタンプがserverDataより新しい場合、localDataを採用

  const localTimestamp = localData.updated_at ? new Date(localData.updated_at).getTime() : 0
  const serverTimestamp = serverData.updated_at ? new Date(serverData.updated_at).getTime() : 0

  if (localTimestamp > serverTimestamp) {
    console.log('Resolving conflict: using local data (newer)')
    return localData
  } else {
    console.log('Resolving conflict: using server data (newer)')
    return serverData
  }
}
